import {
  Button,
  Alert as MuiAlert,
  Link as MuiLink,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components/macro";

import useAuth from "../../hooks/useAuth";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);

const Instruction = styled(Typography)`
  color: ${(props) => props.theme.palette.text.secondary};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing(4)};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing(4)};
  margin-top: ${(props) => props.theme.spacing(4)};
`;

export const REDIRECT_AFTER_LOGIN = "/store-products-analysis";

// Mirrors the browser's own type="email" rule rather than trying to be
// stricter: the backend is what decides whether an address exists.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function OTPSignIn() {
  const navigate = useNavigate();
  const { startLogin, completeLogin } = useAuth();

  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [session, setSession] = useState("");
  const [destination, setDestination] = useState("");
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [fieldError, setFieldError] = useState("");

  const requestCode = async (address) => {
    const result = await startLogin(address);
    setSession(result.session);
    setDestination(result.destination);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!EMAIL_RE.test(address)) {
      setFieldError("Enter a valid email address.");
      return;
    }
    setFieldError("");
    setIsSubmitting(true);
    setError("");
    try {
      await requestCode(address);
      setStep("code");
    } catch (err) {
      setError(err.message || "Could not send a code. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const entered = code.trim();
    if (!entered) {
      setFieldError("Enter the code we emailed you.");
      return;
    }
    setFieldError("");
    setIsSubmitting(true);
    setError("");
    try {
      await completeLogin(email.trim(), entered, session);
      navigate(REDIRECT_AFTER_LOGIN);
    } catch (err) {
      setError(err.message || "Could not verify the code. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Asks the backend for a fresh code. The old one stays valid until it
  // expires, so this is safe to press twice.
  const handleResend = async () => {
    setIsSubmitting(true);
    setError("");
    setNotice("");
    setFieldError("");
    try {
      await requestCode(email.trim());
      setCode("");
      setNotice("We sent a new code.");
    } catch (err) {
      setError(err.message || "Could not send a new code. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setCode("");
    setSession("");
    setDestination("");
    setError("");
    setNotice("");
    setFieldError("");
  };

  if (step === "email") {
    return (
      <form noValidate onSubmit={handleSendCode}>
        <Instruction variant="body2">
          Enter your email and we will send you a one-time code.
        </Instruction>
        {error && (
          <Alert mt={2} mb={3} severity="warning">
            {error}
          </Alert>
        )}
        <TextField
          type="email"
          name="email"
          label="Email Address"
          value={email}
          error={Boolean(fieldError)}
          helperText={fieldError}
          fullWidth
          onChange={(e) => setEmail(e.target.value)}
          my={3}
          autoComplete="email"
          autoFocus
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Sending code..." : "Email a one-time code"}
        </Button>
      </form>
    );
  }

  return (
    <form noValidate onSubmit={handleVerifyCode}>
      <Instruction variant="body2">
        {destination
          ? `We sent a code to ${destination}. It expires shortly.`
          : "Enter the code we emailed you. It expires shortly."}
      </Instruction>
      {error && (
        <Alert mt={2} mb={3} severity="warning">
          {error}
        </Alert>
      )}
      {notice && !error && (
        <Alert mt={2} mb={3} severity="success">
          {notice}
        </Alert>
      )}
      <TextField
        type="text"
        name="code"
        label="Sign-in code"
        value={code}
        error={Boolean(fieldError)}
        helperText={fieldError}
        fullWidth
        onChange={(e) => setCode(e.target.value)}
        my={3}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          autoComplete: "one-time-code",
          style: { letterSpacing: "0.4em", fontSize: "1.25rem" },
        }}
        autoFocus
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Verifying..." : "Verify and sign in"}
      </Button>
      <Actions>
        <MuiLink
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={handleResend}
          disabled={isSubmitting}
        >
          Resend code
        </MuiLink>
        <MuiLink
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={handleChangeEmail}
          disabled={isSubmitting}
        >
          Use a different email
        </MuiLink>
      </Actions>
    </form>
  );
}

export default OTPSignIn;
